import dayjs from 'dayjs';
import { useEffect, useMemo } from 'react';

import type { AgentInfo } from '@/features/appointments/utils/AgentInfo';
import { shuffleArray } from '@/features/appointments/utils/shuffleArray';
import type { TimeSlot } from '@/features/appointments/utils/TimeSlot';
import type { NearbyAgentInfo } from '@/features/scrollFlow/pages/Calendar/api/getNearbyAgents';
import useAppointmentStore, {
  isInBranchAppointment as isInBranchSelector,
} from '@/store/AppointmentStore';
import useResolvedAgentsQuery from './useResolvedAgentsQuery';

export interface AvailableAgent extends Omit<AgentInfo, 'agentSpecialties'> {
  agentSpecialties?: string[];
  distance?: number;
  slotStartTimes: Set<string>;
}

const normalizeAgent = (agent: AgentInfo | NearbyAgentInfo): AvailableAgent => {
  const hasDistance = 'distance' in agent;

  return {
    ...agent,
    distance: hasDistance ? agent.distance : undefined,
    slotStartTimes: new Set(agent.timeSlots.map((slot) => slot.startTime)),
  };
};

const getUniqueSortedSlots = (timeSlots: TimeSlot[]): TimeSlot[] =>
  Object.values(
    Object.fromEntries(timeSlots.map((slot) => [slot.startTime, slot])),
  ).sort((a, b) => dayjs(a.startTime).diff(dayjs(b.startTime)));

const useAgentsAndTimeslots = () => {
  const selectedStartTime = useAppointmentStore(
    (state) => state.selectedStartTime,
  );
  const selectedAgent = useAppointmentStore((state) => state.selectedAgent);
  const setSelectedAgent = useAppointmentStore(
    (state) => state.setSelectedAgent,
  );
  const isInBranch = useAppointmentStore(isInBranchSelector);

  const effectiveQuery = useResolvedAgentsQuery();

  const agents = useMemo(() => {
    if (!effectiveQuery.data) return [];

    return effectiveQuery.data.map(normalizeAgent);
  }, [effectiveQuery.data]);

  const timeSlots = useMemo(
    () => getUniqueSortedSlots(agents.flatMap((agent) => agent.timeSlots)),
    [agents],
  );

  const agentsForSelectedTimeSlot = useMemo(() => {
    if (!selectedStartTime) return [];

    const filtered = agents.filter((agent) =>
      agent.slotStartTimes.has(selectedStartTime),
    );

    // For in-branch agents, preserve the backend-provided ordering (city match first, then distance)
    if (isInBranch) {
      return filtered;
    }

    // For online appointments, shuffle to randomize agent presentation
    return shuffleArray(filtered);
  }, [agents, selectedStartTime, isInBranch]);

  useEffect(() => {
    if (!selectedAgent?.agentRecordId) return;
    const isStillAvailable = agentsForSelectedTimeSlot.some(
      (agent) =>
        agent.agentRecordId === selectedAgent.agentRecordId &&
        agent.serviceTerritoryId === selectedAgent.serviceTerritoryId,
    );
    if (!isStillAvailable) {
      setSelectedAgent({});
    }
  }, [agentsForSelectedTimeSlot, selectedAgent, setSelectedAgent]);

  return {
    agents,
    agentsForSelectedTimeSlot,
    timeSlots,
    isLoading: effectiveQuery.isFetching,
    error: effectiveQuery.error,
    refetch: effectiveQuery.refetch,
  };
};

export default useAgentsAndTimeslots;
